import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { HeaderErrorEnum } from '../enums/header-error.enum';
import { SubUrlsEnum } from '../enums/sub-urls.enum';
import { IAdmin, IAdminDTOOut } from '../interfaces/users/administrator.interface';
import { IContact, IContactDTOIn, IContactDTOOut, IContactsDTOIn } from '../interfaces/users/contact.interface';
import { IEmployee, IEmployeeDTOIn, IEmployeeDTOOut } from '../interfaces/users/employee.interface';
import { IParent, IParentDTOIn, IParentDTOOut } from '../interfaces/users/parent.interface';
import { IRelative, IRelativeDTOIn, IRelativeDTOOut } from '../interfaces/users/relative.interface';
import { IStudent, IStudentDTOIn, IStudentDTOOut } from '../interfaces/users/student.interface';
import { IMyUserDTOOut, IUser, IUserDTOIn, IUsersFromBack, IUsersLightFromBack } from '../interfaces/users/user.interface';
import { MappersService } from './mappers.service';

@Injectable({
    providedIn: 'root',
})
export class UsersService {
    constructor(private http: HttpClient, private mapperService: MappersService) {}

    // cRud: READ
    public getAllUsersLight(studentId?: number): Observable<IUsersLightFromBack> {
        let params: HttpParams = new HttpParams();
        if (studentId) {
            params = params.set('viewing_student_id', studentId);
        }

        return this.http.get<IUsersLightFromBack>(environment.wasoApi + SubUrlsEnum.USERS_REPERTOIRE, {
            params: studentId ? params : null,
        });
    }

    public getAllUsers(studentId?: number): Observable<IUsersFromBack> {
        let params: HttpParams = new HttpParams();
        if (studentId) {
            params = params.set('viewing_student_id', studentId);
        }

        return this.http.get<IUsersFromBack>(environment.wasoApi + SubUrlsEnum.USERS, { params: studentId ? params : null });
    }

    public getAllEmployees(studentId?: number): Observable<IEmployee[]> {
        let params: HttpParams = new HttpParams();
        if (studentId) {
            params = params.set('viewing_student_id', studentId);
        }
        return this.http
            .get<IUsersFromBack>(environment.wasoApi + SubUrlsEnum.EMPLOYEES, { params: studentId ? params : null })
            .pipe(
                map((users: IUsersFromBack) =>
                    users.employees.map((user: IEmployeeDTOIn) =>
                        this.mapperService.castIUserToIEmployee(this.mapperService.fromIUserDTOInToIUser(user))
                    )
                )
            );
    }

    public getAllParents(studentId?: number): Observable<IParent[]> {
        let params: HttpParams = new HttpParams();
        if (studentId) {
            params = params.set('viewing_student_id', studentId);
        }
        return this.http
            .get<IUsersFromBack>(environment.wasoApi + SubUrlsEnum.PARENTS, { params: studentId ? params : null })
            .pipe(
                map((users: IUsersFromBack) =>
                    users.parents.map((user: IParentDTOIn) =>
                        this.mapperService.castIUserToIParent(this.mapperService.fromIUserDTOInToIUser(user))
                    )
                )
            );
    }

    public getAllStudents(studentId?: number): Observable<IStudent[]> {
        let params: HttpParams = new HttpParams();
        if (studentId) {
            params = params.set('viewing_student_id', studentId);
        }
        return this.http
            .get<IUsersFromBack>(environment.wasoApi + SubUrlsEnum.STUDENTS, { params: studentId ? params : null })
            .pipe(
                map((users: IUsersFromBack) =>
                    users.students.map((user: IStudentDTOIn) =>
                        this.mapperService.castIUserToIStudent(this.mapperService.fromIUserDTOInToIUser(user))
                    )
                )
            );
    }

    public getUserRelatives(studentId?: number): Observable<IRelative[]> {
        let params: HttpParams = new HttpParams();
        if (studentId) {
            params = params.set('viewing_student_id', studentId);
        }
        return this.http
            .get<IUsersFromBack>(environment.wasoApi + SubUrlsEnum.RELATIVES, { params: studentId ? params : null })
            .pipe(
                map((users: IUsersFromBack) =>
                    users.relatives.map((user: IRelativeDTOIn) =>
                        this.mapperService.castIUserToIRelative(this.mapperService.fromIUserDTOInToIUser(user))
                    )
                )
            );
    }

    public getAllContacts(studentId?: number): Observable<IContact[]> {
        let params: HttpParams = new HttpParams();
        if (studentId) {
            params = params.set('student_id', studentId);
        }

        return this.http
            .get<IContactsDTOIn>(environment.wasoApi + SubUrlsEnum.CONTACTS, { params: studentId ? params : null })
            .pipe(
                map((contacts: IContactsDTOIn) =>
                    contacts.contacts.map((user: IContactDTOIn) =>
                        this.mapperService.castIUserToIContact(this.mapperService.fromIUserDTOInToIUser(user))
                    )
                )
            );
    }

    public getParentById(id: number): Observable<IUser> {
        return this.http
            .get<IUserDTOIn>(environment.wasoApi + SubUrlsEnum.PARENTS + '/' + id)
            .pipe(map((user: IUserDTOIn) => this.mapperService.fromIUserDTOInToIUser(user)));
    }

    public getEmployeeById(id: number): Observable<IUser> {
        return this.http
            .get<IUserDTOIn>(environment.wasoApi + SubUrlsEnum.EMPLOYEES + '/' + id)
            .pipe(map((user: IUserDTOIn) => this.mapperService.fromIUserDTOInToIUser(user)));
    }

    public getStudentById(id: number): Observable<IUser> {
        return this.http
            .get<IUserDTOIn>(environment.wasoApi + SubUrlsEnum.STUDENTS + '/' + id)
            .pipe(map((user: IUserDTOIn) => this.mapperService.fromIUserDTOInToIUser(user)));
    }

    public getAdministratorById(id: number): Observable<IUser> {
        return this.http
            .get<IUserDTOIn>(environment.wasoApi + SubUrlsEnum.ADMINISTRATORS + '/' + id)
            .pipe(map((user: IUserDTOIn) => this.mapperService.fromIUserDTOInToIUser(user)));
    }

    public getRelativeById(id: number): Observable<IUser> {
        return this.http
            .get<IUserDTOIn>(environment.wasoApi + SubUrlsEnum.RELATIVES + '/' + id)
            .pipe(map((user: IUserDTOIn) => this.mapperService.fromIUserDTOInToIUser(user)));
    }

    public getContactById(id: number): Observable<IUser> {
        return this.http
            .get<IUserDTOIn>(environment.wasoApi + SubUrlsEnum.CONTACTS + '/' + id)
            .pipe(map((user: IUserDTOIn) => this.mapperService.fromIUserDTOInToIUser(user)));
    }

    // Crud: CREATE
    public createEmployee(newEmployee: IEmployeeDTOOut): Observable<IEmployee> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http
            .post<IUserDTOIn>(environment.wasoApi + SubUrlsEnum.EMPLOYEES, newEmployee, { headers })
            .pipe(map((user: IUserDTOIn) => this.mapperService.castIUserToIEmployee(this.mapperService.fromIUserDTOInToIUser(user))));
    }

    public createAdministrator(newAdministrator: IAdminDTOOut): Observable<IAdmin> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http
            .post<IUserDTOIn>(environment.wasoApi + SubUrlsEnum.ADMINISTRATORS, newAdministrator, { headers })
            .pipe(map((user: IUserDTOIn) => this.mapperService.castIUserToIAdmin(this.mapperService.fromIUserDTOInToIUser(user))));
    }

    public createParent(newParent: IParentDTOOut): Observable<IParent> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http
            .post<IUserDTOIn>(environment.wasoApi + SubUrlsEnum.PARENTS, newParent, { headers })
            .pipe(map((user: IUserDTOIn) => this.mapperService.castIUserToIParent(this.mapperService.fromIUserDTOInToIUser(user))));
    }

    public createStudent(newStudent: IStudentDTOOut): Observable<IStudent> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http
            .post<IUserDTOIn>(environment.wasoApi + SubUrlsEnum.STUDENTS, newStudent, { headers })
            .pipe(map((user: IUserDTOIn) => this.mapperService.castIUserToIStudent(this.mapperService.fromIUserDTOInToIUser(user))));
    }

    public createRelative(newRelative: IRelativeDTOOut): Observable<IRelative> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http
            .post<IUserDTOIn>(environment.wasoApi + SubUrlsEnum.RELATIVES, newRelative, { headers })
            .pipe(map((user: IUserDTOIn) => this.mapperService.castIUserToIRelative(this.mapperService.fromIUserDTOInToIUser(user))));
    }

    public createContact(newContact: IContactDTOOut): Observable<IContact> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http
            .post<IUserDTOIn>(environment.wasoApi + SubUrlsEnum.CONTACTS, newContact, { headers })
            .pipe(map((user: IUserDTOIn) => this.mapperService.castIUserToIContact(this.mapperService.fromIUserDTOInToIUser(user))));
    }

    // crUd: UPDATE
    public patchMyAccount(myUser: IMyUserDTOOut): Observable<IUser> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http
            .patch<IUserDTOIn>(environment.wasoApi + SubUrlsEnum.MY_ACCOUNT, myUser, { headers })
            .pipe(map((user: IUserDTOIn) => this.mapperService.fromIUserDTOInToIUser(user)));
    }

    public patchEmployee(employee: IEmployeeDTOOut, id: number): Observable<IEmployee> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http
            .patch<IUserDTOIn>(environment.wasoApi + SubUrlsEnum.EMPLOYEES + '/' + id, employee, {
                headers,
            })
            .pipe(map((user: IUserDTOIn) => this.mapperService.castIUserToIEmployee(this.mapperService.fromIUserDTOInToIUser(user))));
    }

    public patchAdministrator(admin: IAdminDTOOut, id: number): Observable<IAdmin> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http
            .patch<IUserDTOIn>(environment.wasoApi + SubUrlsEnum.ADMINISTRATORS + '/' + id, admin, {
                headers,
            })
            .pipe(map((user: IUserDTOIn) => this.mapperService.castIUserToIAdmin(this.mapperService.fromIUserDTOInToIUser(user))));
    }

    public patchParent(parent: IParentDTOOut, id: number): Observable<IParent> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http
            .patch<IUserDTOIn>(environment.wasoApi + SubUrlsEnum.PARENTS + '/' + id, parent, { headers })
            .pipe(map((user: IUserDTOIn) => this.mapperService.castIUserToIParent(this.mapperService.fromIUserDTOInToIUser(user))));
    }

    public patchStudent(student: IStudentDTOOut, id: number): Observable<IStudent> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        let params: HttpParams = new HttpParams();
        params = params.set('viewing_student_id', id);
        return this.http
            .patch<IUserDTOIn>(environment.wasoApi + SubUrlsEnum.STUDENTS + '/' + id, student, {
                headers,
                params,
            })
            .pipe(map((user: IUserDTOIn) => this.mapperService.castIUserToIStudent(this.mapperService.fromIUserDTOInToIUser(user))));
    }

    public patchRelative(relative: IRelativeDTOOut, id: number): Observable<IRelative> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http
            .patch<IUserDTOIn>(environment.wasoApi + SubUrlsEnum.RELATIVES + '/' + id, relative, {
                headers,
            })
            .pipe(map((user: IUserDTOIn) => this.mapperService.castIUserToIRelative(this.mapperService.fromIUserDTOInToIUser(user))));
    }

    public patchContact(relative: IContactDTOOut, id: number): Observable<IContact> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http
            .patch<IUserDTOIn>(environment.wasoApi + SubUrlsEnum.CONTACTS + '/' + id, relative, {
                headers,
            })
            .pipe(map((user: IUserDTOIn) => this.mapperService.castIUserToIContact(this.mapperService.fromIUserDTOInToIUser(user))));
    }

    // DELETE
    public deleteAdministrator(id: number): Observable<void> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http.delete<void>(environment.wasoApi + SubUrlsEnum.ADMINISTRATORS + '/' + id, {
            headers,
        });
    }

    public deleteEmployee(id: number): Observable<void> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http.delete<void>(environment.wasoApi + SubUrlsEnum.EMPLOYEES + '/' + id, {
            headers,
        });
    }

    public deleteParent(id: number): Observable<void> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http.delete<void>(environment.wasoApi + SubUrlsEnum.PARENTS + '/' + id, {
            headers,
        });
    }

    public deleteStudent(id: number): Observable<void> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http.delete<void>(environment.wasoApi + SubUrlsEnum.STUDENTS + '/' + id, {
            headers,
        });
    }

    public deleteRelative(id: number): Observable<void> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http.delete<void>(environment.wasoApi + SubUrlsEnum.RELATIVES + '/' + id, {
            headers,
        });
    }

    public deleteContact(id: number): Observable<void> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http.delete<void>(environment.wasoApi + SubUrlsEnum.CONTACTS + '/' + id, {
            headers,
        });
    }

    // OTHERS
    public getSortedStudentsPointings(): Observable<IStudent[]> {
        return this.http
            .get<IUsersFromBack>(environment.wasoApi + SubUrlsEnum.NURSERY_SORTED_STUDENTS)
            .pipe(
                map((users: IUsersFromBack) =>
                    users.students.map((user: IStudentDTOIn) =>
                        this.mapperService.castIUserToIStudent(this.mapperService.fromIUserDTOInToIUser(user))
                    )
                )
            );
    }

    public getParentInCharge(studentId: number, date?: string, period?: string): Observable<number> {
        let params: HttpParams = new HttpParams();
        if (date) {
            params = params.set('date', date);
        }
        if (period) {
            params = params.set('period', period);
        }
        return this.http
            .get<{ parent_id: number }>(environment.wasoApi + SubUrlsEnum.PARENT_IN_CHARGE.replace('{{studentId}}', studentId.toString()), {
                params: params ? params : null,
            })
            .pipe(map((parentInCharge: { parent_id: number }) => parentInCharge.parent_id));
    }

    // cruD: DELETE -> ENABLE / DISABLE
    public enableUser(userId: number): Observable<IUser> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        const fullUrl: string = environment.wasoApi + SubUrlsEnum.USERS + '/' + userId + '/' + SubUrlsEnum.STATUS;
        return this.http
            .post<IUserDTOIn>(fullUrl, {}, { headers })
            .pipe(map((user: IUserDTOIn) => this.mapperService.fromIUserDTOInToIUser(user)));
    }

    public disableUser(userId: number): Observable<IUser> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        const fullUrl: string = environment.wasoApi + SubUrlsEnum.USERS + '/' + userId + '/' + SubUrlsEnum.STATUS;
        return this.http
            .delete<IUserDTOIn>(fullUrl, { headers })
            .pipe(map((user: IUserDTOIn) => this.mapperService.fromIUserDTOInToIUser(user)));
    }
}
